import type {
  Capability,
  CurrentUser,
  UnscopedCapability,
} from "@opendialysis/procare-api-schemas";

/**
 * UI gating over SERVER-COMPUTED conclusions.
 *
 * The API enforces authorization now; hiding a control here is UX, not the
 * guard. This file deliberately EVALUATES nothing: the server collapses
 * superadmin, the admin flag, the profession matrix and study membership into
 * `capabilities` on /auth/me and a `permissions` map on scoped resources, and
 * this module only reads those conclusions back. Recomputing them client-side
 * from raw inputs is the tempting "fix" that must not happen: a login-time
 * snapshot goes stale the moment a steward revokes a membership, and every
 * future scoping input (departments, panels) would become a client change. The
 * known cost: a user in ANOTHER browser session keeps stale buttons until her
 * next refetch, and the API refuses her anyway.
 */

/**
 * Global gating: may this user do this anywhere (nav items, create buttons).
 *
 * Study-scoped capabilities are not askable here, BY TYPE: `/auth/me` does not
 * carry them, because "may enrol" has no answer outside one study. Ask
 * `allowed(study, ...)` instead — this used to answer a silent `false`, which
 * renders as a hidden button whose absence looks like a permission decision.
 */
export function can(
  user: CurrentUser | null,
  capability: UnscopedCapability,
): boolean {
  return user?.capabilities.includes(capability) ?? false;
}

/**
 * Scoped gating: may this user do this to THIS resource. The conclusion rides
 * on the resource payload (membership-aware for studies), so revoking access
 * flips the button on the next refetch of the resource, not of the session.
 *
 * Deliberately NOT narrowed to the study-scoped half: a patient payload carries
 * conclusions for capabilities that are unscoped today (`EDIT_PATIENTS` and
 * friends, still facility-wide) precisely so the department pass can scope them
 * without a wire change. Answering per-resource is the point, not which half of
 * the vocabulary the key comes from.
 */
export function allowed(
  resource:
    | { permissions?: Partial<Record<Capability, boolean>> }
    | null
    | undefined,
  capability: Capability,
): boolean {
  return resource?.permissions?.[capability] ?? false;
}

/**
 * Admin identity, not a capability: used where the rule is "an admin or a
 * steward", which is deliberately not in the capability vocabulary because it
 * must not be grantable through the mechanism it governs.
 */
export function isAdmin(user: CurrentUser | null): boolean {
  if (!user) return false;
  return user.isSuperAdmin || user.isAdmin;
}
